import { useState, FormEvent, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

const DESTINATIONS = [
  'Torres del Paine',
  'San Pedro de Atacama',
  'Valparaíso',
  'Isla de Pascua',
  'Puerto Varas',
  'Chiloé',
  'Valle del Elqui',
  'Pucón',
  'Antofagasta',
  'Viña del Mar',
];

export default function Contact() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    telefono: '',
    destinos: [] as string[],
    fecha: '',
    viajeros: '1',
    mensaje: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: Event) => {
      const custom = e as CustomEvent<{ destino: string }>;
      setFormData((prev) => ({ ...prev, destinos: [custom.detail.destino] }));
    };
    window.addEventListener('prefill-contact', handler);
    return () => window.removeEventListener('prefill-contact', handler);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleDestino = (dest: string) => {
    setFormData((prev) => ({
      ...prev,
      destinos: prev.destinos.includes(dest)
        ? prev.destinos.filter((d) => d !== dest)
        : [...prev.destinos, dest],
    }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (formData.destinos.length === 0) return;
    setIsSubmitting(true);
    setSubmitStatus('idle');
    try {
      const formBody = new URLSearchParams();
      formBody.append('nombre', formData.nombre);
      formBody.append('email', formData.email);
      formBody.append('telefono', formData.telefono);
      formBody.append('destinos', formData.destinos.join(', '));
      formBody.append('fecha', formData.fecha);
      formBody.append('viajeros', formData.viajeros);
      formBody.append('mensaje', formData.mensaje);
      const response = await fetch('https://readdy.ai/api/form/d6ks7rdv117fnkj2gh60', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString(),
      });
      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ nombre: '', email: '', telefono: '', destinos: [], fecha: '', viajeros: '1', mensaje: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <section id="contacto" className="py-16 md:py-32 px-4 md:px-8 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8 md:mb-12">
          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">{t('contact_title')}</h2>
          <p className="text-base md:text-lg text-gray-600">{t('contact_subtitle')}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
          <form onSubmit={handleSubmit} data-readdy-form id="contact_form" className="space-y-6">
            <div>
              <label htmlFor="nombre" className="block text-sm font-semibold text-gray-700 mb-2">{t('contact_name')}</label>
              <input type="text" id="nombre" name="nombre" value={formData.nombre} onChange={handleChange} required
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-600 focus:border-transparent transition-all text-sm"
                placeholder={t('contact_name_placeholder')} />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">{t('contact_email')}</label>
              <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-600 focus:border-transparent transition-all text-sm"
                placeholder="email@example.com" />
            </div>

            <div>
              <label htmlFor="telefono" className="block text-sm font-semibold text-gray-700 mb-2">{t('contact_phone')}</label>
              <input type="tel" id="telefono" name="telefono" value={formData.telefono} onChange={handleChange} required
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-600 focus:border-transparent transition-all text-sm"
                placeholder={t('contact_phone_placeholder')} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Multi-select Destinations */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">{t('contact_destination')}</label>
                <div className="relative" ref={dropdownRef}>
                  <button
                    type="button"
                    onClick={() => setDropdownOpen((o) => !o)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-600 focus:border-transparent transition-all cursor-pointer text-sm text-left flex items-center justify-between bg-white"
                  >
                    <span className={formData.destinos.length === 0 ? 'text-gray-400' : 'text-gray-800 truncate'}>
                      {formData.destinos.length === 0
                        ? t('contact_destination_select')
                        : formData.destinos.length === 1
                        ? formData.destinos[0]
                        : `${formData.destinos.length} destinations`}
                    </span>
                    <i className={`ri-arrow-${dropdownOpen ? 'up' : 'down'}-s-line text-gray-500 ml-2 flex-shrink-0`}></i>
                  </button>

                  {dropdownOpen && (
                    <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg max-h-56 overflow-y-auto">
                      {DESTINATIONS.map((dest) => {
                        const checked = formData.destinos.includes(dest);
                        return (
                          <label
                            key={dest}
                            className="flex items-center gap-3 px-4 py-2.5 hover:bg-teal-50 cursor-pointer transition-colors"
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleDestino(dest)}
                              className="w-4 h-4 accent-teal-600 cursor-pointer"
                            />
                            <span className="text-sm text-gray-700">{dest}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
                {/* Selected tags */}
                {formData.destinos.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {formData.destinos.map((d) => (
                      <span key={d} className="inline-flex items-center gap-1 bg-teal-100 text-teal-700 text-xs px-2 py-1 rounded-full">
                        {d}
                        <button type="button" onClick={() => toggleDestino(d)} className="hover:text-teal-900 cursor-pointer">
                          <i className="ri-close-line text-xs"></i>
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label htmlFor="fecha" className="block text-sm font-semibold text-gray-700 mb-2">{t('contact_date')}</label>
                <input type="date" id="fecha" name="fecha" value={formData.fecha} onChange={handleChange} required
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-600 focus:border-transparent transition-all cursor-pointer text-sm" />
              </div>
            </div>

            <div>
              <label htmlFor="viajeros" className="block text-sm font-semibold text-gray-700 mb-2">{t('contact_travelers')}</label>
              <select id="viajeros" name="viajeros" value={formData.viajeros} onChange={handleChange} required
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-600 focus:border-transparent transition-all cursor-pointer text-sm">
                {[1, 2, 3, 4, 5, 6, 7, 8].map((num) => (
                  <option key={num} value={num}>
                    {num} {num === 1 ? t('contact_person') : t('contact_persons')}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="mensaje" className="block text-sm font-semibold text-gray-700 mb-2">{t('contact_message')}</label>
              <textarea id="mensaje" name="mensaje" value={formData.mensaje} onChange={handleChange} maxLength={500} rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-600 focus:border-transparent transition-all resize-none text-sm"
                placeholder={t('contact_message_placeholder')}></textarea>
              <p className="text-xs text-gray-500 mt-1">{formData.mensaje.length}/500 {t('contact_chars')}</p>
            </div>

            <button type="submit" disabled={isSubmitting || formData.destinos.length === 0}
              className="w-full py-4 bg-gradient-to-r from-teal-500 to-cyan-600 text-white text-base md:text-lg font-bold rounded-xl hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap">
              {isSubmitting ? t('contact_submitting') : t('contact_submit')}
            </button>

            {submitStatus === 'success' && (
              <div className="p-4 bg-green-50 border border-green-200 rounded-xl text-green-700 text-center text-sm">{t('contact_success')}</div>
            )}
            {submitStatus === 'error' && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-center text-sm">{t('contact_error')}</div>
            )}
          </form>

          <div className="space-y-6">
            <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-14 h-14 bg-teal-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-phone-fill text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2">{t('contact_phone_label')}</h3>
              <a href="tel:+56953318253" className="text-teal-600 hover:underline cursor-pointer">+56 9 5331 8253</a>
            </div>

            <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-14 h-14 bg-teal-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-mail-fill text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2">{t('contact_email_label')}</h3>
              <p className="text-gray-600 text-sm md:text-base break-words">contacto@galitravelchile.com</p>
            </div>

            <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-14 h-14 bg-teal-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-whatsapp-fill text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2">{t('contact_whatsapp')}</h3>
              <a href="https://wa.me/56953318253" target="_blank" rel="noopener noreferrer nofollow" className="text-teal-600 hover:underline cursor-pointer">+56 9 5331 8253</a>
              <p className="text-sm text-gray-500 mt-2">{t('contact_whatsapp_available')}</p>
            </div>

            <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-14 h-14 bg-teal-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-time-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2">{t('contact_hours')}</h3>
              <p className="text-gray-600 text-sm md:text-base">{t('contact_hours_weekdays')}</p>
              <p className="text-gray-600 text-sm md:text-base">{t('contact_hours_saturday')}</p>
            </div>

            <div className="bg-gradient-to-r from-teal-500 to-cyan-600 rounded-2xl p-6 md:p-8 text-white">
              <div className="flex items-center gap-3 mb-4">
                <i className="ri-customer-service-2-line text-2xl md:text-3xl"></i>
                <h3 className="text-lg md:text-xl font-bold">{t('contact_quick_response')}</h3>
              </div>
              <p className="text-white/90 text-sm md:text-base">{t('contact_quick_response_text')}</p>
            </div>

            <div className="flex items-center justify-center gap-4">
              {[
                { icon: 'instagram', url: 'https://www.instagram.com/galitravelchile/' },
              ].map(({ icon, url }) => (
                <a key={icon} href={url} target="_blank" rel="noopener noreferrer nofollow"
                  className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center hover:bg-teal-600 hover:text-white transition-colors cursor-pointer">
                  <i className={`ri-${icon}-fill text-3xl`}></i>
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}